import React, { useState, useContext, useRef } from "react";
import { Stack, Chip, Divider, TableCell, Link, LinkProps, Tooltip, TooltipProps, tooltipClasses, IconButton } from "@mui/material";
import { COLOR_MAP } from "../../../../constants/ColorNames";
import styled from "@emotion/styled";
import { TableCampaignStageSkeleton } from "../skeleton/SkeletonLayout";
import { TABLE_CONSTANTS } from "../../constants";
import { extractDomain } from "../../../../utils/helpers";
import AddIcon from '@mui/icons-material/Add';
import { GreenPassiveBox } from "./TableContent";
import { INFO_ICON } from "../../../../constants/icons/InfoIcon";
import { styled as MUIStyled } from '@mui/material/styles';


type CampaignStagesPropsType = {
  isSelectedCampaign: boolean
  campaignStageData: any
  isExpanded: boolean
  isWhatsAppCampaign: boolean
  revampServices?: {enabled:boolean}
}

const InnerDiv = styled.div`
  padding: 6px 9px 6px 4px;
  align-items: center;
  justify-content: center;
  min-width: 67px;
  max-width: 67px;
  overflow-x: clip;
`;

const Text = styled.p`
  font-size: 1rem;
  line-height: 1.2rem;
  font-weight: 400;
  text-align: center;
  margin: 0;
  color: ${COLOR_MAP.GREY_DARK};
`;

const PercentageText = styled.span`
font-size: 1rem;
font-weight: 400;
line-height: 1.4rem;
`

const BoldText = styled(Text)`
  font-weight: 500;
  font-size: 1.2rem;
  line-height: 1.6rem;
  color: ${COLOR_MAP.GREY_2};
`;

const StatusLabel = styled(Text)`
  margin-bottom: 0.4rem;
  text-align: left;
  display:inline;
`;

const MainDiv = styled(Stack)`
  display: flex;
  flex-direction: row;
  border-radius: 1rem;
  width: fit-content;
`;

const FolderText = (fontColor: string) => styled.p`
  font-size: 1.4rem;
  line-height: 2rem;
  font-weight: 500;
  margin: 0;
  color: ${fontColor};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 220px;
  &:hover {
    color: ${COLOR_MAP.DARK_PURPLE}
  }
`;

const InboundContainer = styled(Stack)`
  width: 76px;  
  padding: 6px 4px;
`

const ToolTipTitleContainer = styled.div`
width: 212px;
font-size:14px;
`

interface StyledLinkProps extends LinkProps {
  stage: string;
}

const StyledLink = styled(Link)(({ stage }: StyledLinkProps) => ({
  textDecoration: 'none',
  cursor: (stage === 'Applied' || stage === 'Replied' || stage === 'Inbound') ? 'pointer' : 'default',
  pointerEvents: (stage === 'Applied' || stage === 'Replied' || stage === 'Inbound') ? 'auto' : 'none',
  textAlign: 'left',
}));

const generateStages = (data: any, isWhatsApp: boolean) => {

  if(isWhatsApp){

    return [
      {
        "label": "Assigned",
        "value": data.assigned,
        "percentage": data.assigned ? 100 : data.assigned,
      },
      {
        "label": "Completed",
        "value": data.completed,
        "percentage": data.completed ? Math.floor((data.completed * 100) / data.assigned) : data.completed
      },
      {
        "label": "Opened",
        "value": data.opened,
        "percentage": data.opened ? Math.floor((data.opened * 100) / data.completed) : data.opened
      },
      {
        "label": "Responded",
        "value": (data.responded || data.responded),
        "percentage": data.responded ? Math.ceil((data.responded * 100) / data.assigned) : (data.responded || 0)
      },
    ]

  }
  return (
    [
      {
        "label": "Assigned",
        "value": data.assigned,
        "percentage": data.assigned ? 100 : data.assigned,
      },
      {
        "label": "Completed",
        "value": data.completed,
        "percentage": data.completed ? Math.floor((data.completed * 100) / data.assigned) : data.completed
      },
      {
        "label": "Opened",
        "value": data.opened,
        "percentage": data.opened ? Math.floor((data.opened * 100) / data.completed) : data.opened
      },
      {
        "label": "Replied",
        "value": data.replied,
        "percentage": data.replied ? Math.ceil((data.replied * 100) / data.completed) : data.replied
      },
      {
        "label": "Applied",
        "value": data.applied,
        "percentage": data.applied ? Math.ceil((data.applied * 100) / data.completed) : data.applied
      },
    ]
  )
}

const BootstrapTooltip = MUIStyled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.arrow}`]: {
    color: '#1D1934',
    left:'4px !important'
    
  },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#1D1934',
    // minWidth:'224px',
    display:'inline-block',
    padding:'8px'
  },
}));


const CampaignStages = (props: CampaignStagesPropsType) => {
  const { isSelectedCampaign, campaignStageData, isExpanded, isWhatsAppCampaign } = props;
  const { isPassiveJob, passiveJobCount} = campaignStageData || {}
  const iconRef = useRef<any>(null);
  const CustomFolderText = FolderText(isSelectedCampaign ? COLOR_MAP.DARK_PURPLE : COLOR_MAP.GREY_1);

  if (!campaignStageData) return <TableCampaignStageSkeleton />

  const stages = generateStages(campaignStageData, isWhatsAppCampaign);

  const redirectToFolder = () => {
    const url = "recruiter-fm/folder-details";
    return (`${extractDomain()}/edge/${url}?campaignId=${campaignStageData?.campaignId||""}&campaign=${campaignStageData?.campaignName || ""}&folderID=${campaignStageData?.folderId}&autoMatch=${!!campaignStageData?.autoMatch}`)
  }

  const redirectToPassiveJobFolder = () => {
    const url = "recruiter-fm/folder-details";
    return (`${extractDomain()}/edge/${url}?campaignId=${campaignStageData?.campaignId||""}&campaign=${campaignStageData?.campaignName || ""}&folderID=${campaignStageData?.jobFolderId}&autoMatch=${!!campaignStageData?.autoMatch}&tab=inbound`)
  }

  const TooltipContent = <BootstrapTooltip
 placement="top-start"
 title={
   <ToolTipTitleContainer>
      Extra applicants are here from the inbound job posted for your campaign
   </ToolTipTitleContainer>
 }
 arrow
 PopperProps={{
   modifiers: [
     {
       name: 'offset',
       options: {
         offset: [-180, -4], 
       },
     },
   ],
 }}

>
 <IconButton aria-label="information" ref={iconRef} style={{display:'inline'}}>
     <INFO_ICON/>
 </IconButton>
</BootstrapTooltip>

  const InboundTooltip = <BootstrapTooltip
  placement="bottom"
  title={
    <div style={{fontSize:'14px'}}>
    {campaignStageData?.applied ? (Number(passiveJobCount) / Number(campaignStageData?.applied))?.toFixed(1) : 1}x of applies
    </div>
  }
  arrow


  >
  <IconButton aria-label="information" ref={iconRef} style={{display:'inline'}}>
  <GreenPassiveBox style={{padding: '1px'}}>{campaignStageData?.applied ? (Number(passiveJobCount) / Number(campaignStageData?.applied))?.toFixed(1) : 1}x</GreenPassiveBox>
  </IconButton>
  </BootstrapTooltip>

  return (
      <TableCell align="left" sx={{
        width: TABLE_CONSTANTS.CAMPAIGN_STAGES,
        padding: '2.4rem',
        paddingTop: isExpanded ? '3.8rem' : '2.4rem',
        paddingBottom: isExpanded ? 0 : '1.6rem',
        borderBottom: 'none',
        transition: 'all 0.2s ease',
        display: 'flex'
      }} size="small">
        <MainDiv>
          <Stack direction="row" 
          alignItems={'center'}
          sx={{background: COLOR_MAP.GREY_8, borderRadius: `4px 4px ${isExpanded? '0 0': '4px 4px'}`, width: '400px'}}
          >
            {stages.map((stage, index) => {
              return (
                <StyledLink
                  className="primary"
                  underline="hover"
                  display={"inline-flex"}
                  alignItems={"center"}
                  href={redirectToFolder()}
                  target="_blank"
                  color="black"
                  stage={stage.label}
                  borderRight={index === stages.length - 1 ? 'none' : `0.5px dashed ${COLOR_MAP.GREY_5}`} 
                  {...(isExpanded && {
                    height: '100%',
                    boxSizing:"border-box",
                    alignItems:'flex-start',
                    paddingTop: "10px"
                  })}
                >
                  <InnerDiv style={{
                    ...(isWhatsAppCampaign && {
                      minWidth: '85px',   
                      maxWidth: '85px'      
                    })  
                  }}>
                    <StatusLabel>{stage.label}</StatusLabel>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="flex-start"
                      spacing={1}
                    >
                      <BoldText>{stage.value}</BoldText>
                      <PercentageText>({stage.percentage}%)</PercentageText>
                    </Stack>
                  </InnerDiv>
                </StyledLink>
              );
            })}
          </Stack>
          {isPassiveJob && (<Stack direction='row' display="flex" alignItems="center" gap="0.4rem">
            <AddIcon sx={{paddingLeft: '5px'}}/>
            <Stack sx={{ flex: 1,height:'100%', background: isSelectedCampaign ? 'white' : COLOR_MAP.WARNING_YELLOW, borderRadius: `4px 4px ${isExpanded? '0 0': '4px 4px'}` }}>
              <InboundContainer sx={{  height:'100%', display:'flex',alignItems:'center', justifyContent:'center' }}>
                <StyledLink
                  className="primary"
                  underline="hover"
                  display="flex"
                  flexDirection="column"
                  alignItems={"center"}
                  href={campaignStageData?.jobFolderId ? redirectToPassiveJobFolder() : redirectToFolder()}
                  target="_blank"
                  color="black"
                  stage='Inbound'
                >
                  
                  <div style={{display:'flex',flexDirection:'row',alignItems:'start',gap:'4px'}}>
                  <StatusLabel>Inbound Applies</StatusLabel>
                  {TooltipContent}
                  </div>
                  <Stack direction="row" gap="4px" alignItems="center">
                    <BoldText>{passiveJobCount}</BoldText>
                    {!isExpanded && (
                      InboundTooltip
                    )}
                  </Stack>
                </StyledLink>
              </InboundContainer>
            </Stack>
          </Stack>)}
        </MainDiv>
      </TableCell>
  );
};

export default CampaignStages;
